import React, { useState, useEffect, useRef, useContext } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale, setDefaultLocale } from "react-datepicker";
import enGB from 'date-fns/locale/en-GB'; // the locale you want
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Select, { components } from 'react-select';
import UserService from '../../Services/user.service.js';
import ClientService from '../../Services/client.service.js';
import SubAccountantService from '../../Services/subAccountant.service.js';
import TaxAuthorityService from '../../Services/taxAuthority.service.js';
import PuffLoader from "react-spinners/PuffLoader";
import EnumsService from '../../Services/enums.service.js';
import CustomDeleteConfirmationModal from '../../Components/CustomDeleteConfirmationModal/CustomDeleteConfirmationModal.js';
import ProfileDocumentIcon from "../../assests/images/details.png"; // Ensure you have the correct path
import accountantIcon from "../../assests/images/accountantGroup.png"; // Ensure you have the correct path
// import clientsIcon from "../../assests/images/clientsGroups.png"; // Ensure you have the correct path
import { useBack } from "use-back";
import PropertiesIcon from "../../assests/images/properties.png";
import { useTranslation } from 'react-i18next';
import styles from './AccountantDetails.module.scss';
import { ToastContainer, toast } from "react-toastify";
import Constants from '../../assests/Constants/constants.js';
import 'react-toastify/dist/ReactToastify.css';
import { Cancel } from '@mui/icons-material';
import {
    IconButton,
    Tooltip, TextField, Button, InputAdornment
} from '@mui/material';
import { beautifyNumber } from '../../Utils/FormatNumber.js';
import clientsIcon from "../../assests/images/Main/clients.svg";
import subAccountantsIcon from "../../assests/images/Main/subAccountants.svg";
import editIconImage from "../../assests/images/Icons/editIcon.svg";
import addIconImage from "../../assests/images/Icons/addIcon.svg";
import importIconImage from "../../assests/images/Icons/importIcon.svg";
import AutocompleteCardSelect from '../../Components/Widgets/AutocompleteCardSelect/AutocompleteCardSelect.jsx';
import AccountantDetailsModals from '../../Components/AccountantDetails/Modals/AccountantDetailsModals.jsx';
import ProfilePictureDialog from '../../Components/Widgets/ProfilePictureDialog/ProfilePictureDialog.jsx';
import DataTableDialog from '../../Components/AccountantDetails/Modals/DataTableDialog.jsx';
import AccountantTaxAuthoritySection from '../../Components/AccountantDetails/SubComponents/AccountantTaxAuthoritySection.jsx';
import SortCodeUsageModal from '../../Components/AccountantDetails/Modals/SortCodeUsageModal.jsx';
import OtherDocumentsTitlesCard from '../../Components/AccountantDetails/SubComponents/OtherDocumentsTitlesCard.jsx';
import DownloadIcon from "@mui/icons-material/Download";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useDirectionByLanguage } from '../../hooks/useDirectionByLanguage.js';
import TableViewIcon from '@mui/icons-material/TableView';
import { handleDownloadTemplate } from '../../Utils/handleDownloadTemplate.js';
import accountingGroupFile from "../../assests/templates/Xtram_AccountingGroup_Template.xlsx";
import palCodeFile from "../../assests/templates/Xtram_Palcode_Template.xlsx";
import palCodeCategoryFile from "../../assests/templates/Xtram_PalCodeCategory_Template.xlsx";
import sortCodesFile from "../../assests/templates/Xtram_SortCodes_template.xlsx";
import { NavHistoryContext } from '../../Contexts/NavHistoryContext.js';
registerLocale('en-GB', {
    ...enGB,
    localize: {
        ...enGB.localize,
        month: n => n + 1, // Display months as numbers starting from 1
    },
    formatLong: {
        ...enGB.formatLong,
        date: () => 'MM/yyyy', // Date format when the picker is not showing
    },
});

setDefaultLocale('en-GB'); // Set the default locale to use it

/** Shared compact profile TextField shell (Contact + MASAV card). */
const profileCompactInputSx = {
    height: '42px',
    padding: '0 12px',
    borderRadius: '10px',
    background: '#F4F7F9',
    border: '1px solid #E8EEF3',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
    '&:focus-within': {
        border: '1.5px solid #2563EB',
        background: '#FFFFFF',
        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
    },
};

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '48px',
        borderRadius: '8px',
        background: '#BFC8FF',
        border: 'none',
        boxShadow: 'none',
        padding: '0 10px',
        textAlign: 'left',
        border: '1px solid transparent',
        '&:focus-within': {
            border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        textAlign: 'left',
        color: 'black'
    }),
    singleValue: (provided) => ({
        ...provided,
        textAlign: 'left',
        color: 'black',
    }),
    menu: (provided) => ({
        ...provided,
        textAlign: 'left',
        color: 'black',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? '#F9AA2A'
            : state.isFocused
                ? '#F9AA2A'
                : null,
        color: 'black',
    }),
    dropdownIndicator: (provided, state) => ({
        ...provided,
        color: state.isFocused ? 'darkgray' : 'black',
        '&:hover': {
            color: 'darkgray',
        },
    }),
};

const AccountantDetails = () => {

    const navigate = useNavigate();
    const { i18n, t } = useTranslation();
    const direction = useDirectionByLanguage(i18n);

    const [user, setUser] = useState({});
    const [token, setToken] = useState({});
    const [clientsCount, setClientsCount] = useState(0);
    const [employeCount, setEmployeCount] = useState(0);
    const [isAccountant, setIsAccountant] = useState(false);

    const [isEditing, setIsEditing] = useState(false);

    const [phoneNumber, setPhoneNumber] = useState('');
    const [name, setName] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [itemIdToDelete, setItemIdToDelete] = useState(null);

    const [email, setEmail] = useState('');
    const [pricePerHour, setPricePerHour] = useState('');
    const [masavInstitutionSubjectCode, setMasavInstitutionSubjectCode] = useState('');
    const [masavSenderInstitutionCode, setMasavSenderInstitutionCode] = useState('');
    const [masavInstitutionName, setMasavInstitutionName] = useState('');
    const [masavBeneficiaryAccountType, setMasavBeneficiaryAccountType] = useState('');
    const [masavHeaderSerial, setMasavHeaderSerial] = useState('');
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    // const [caseTypeOptions, setCaseTypeOptions] = useState([]);
    // const [checksTypeOptions, setChecksTypeOptions] = useState([]);
    const [otherDocumentsTypeOptions, setOtherDocumentsTypeOptions] = useState([]);

    const [caseType, setCaseType] = useState('');
    const [checkType, setCheckType] = useState('');
    const [otherDocumentsType, setOtherDocumentsType] = useState('');
    const [newSortCodeGroup, setNewSortCodeGroup] = useState('');
    const [newSortCodeGroupListNumber, setNewSortCodeGroupListNumber] = useState('');
    const [sortCodeGroupProfitName, setsortCodeGroupProfitName] = useState('');
    const [selectedValues, setSelectedValues] = useState([]);

    const [isPropCaseTypeAdding, setIsPropCaseTypeAdding] = useState(false);
    const [isPropCaseTypeEditing, setIsPropCaseTypeEditing] = useState(false);

    const [isPropChecksTypeAdding, setIsPropChecksTypeAdding] = useState(false);
    const [isPropChecksTypeEditing, setIsPropChecksTypeEditing] = useState(false);

    const [isPropOtherDocumentsTypeAdding, setIsPropOtherDocumentsTypeAdding] = useState(false);
    const [isPropOtherDocumentsTypeEditing, setIsPropOtherDocumentsTypeEditing] = useState(false);

    const [isPropSortCodeGroupAdding, setIsPropSortCodeGroupAdding] = useState(false);
    const [isPropSortCodeGroupEditing, setIsPropSortCodeGroupEditing] = useState(false);

    const [loading, setLoading] = useState(false);
    const [taxAuthRefreshNonce, setTaxAuthRefreshNonce] = useState(0);
    const [editedItem, setEditedItem] = useState(null);

    const [isPropSortCodeAdding, setIsPropSortCodeAdding] = useState(false);
    const [sortCode, setSortCode] = useState({
        sortCodeId: '',
        sortCodeName: '',
        sortCodeCredit: '',
        sortCodeDebit: '',
        sortCodeGroup: null,
        sortCodeVatType: null,
        vat: null,
        participatesInProfitAndLoss: null,
        pcnTransactionType: null,
        code6111Id: null,
        inventoryType: 0,
    });
    const [isPropSortCodeEditing, setIsPropSortCodeEditing] = useState(false);
    const [sortCodesOptions, setSortCodesOptions] = useState([]);

    const [sortCodesGroupOptions, setSortCodesGroupOptions] = useState([]);
    const [sortCodeGroup, setSortCodeGroup] = useState({
        sortCodeGroupName: '',
        sortCodeGroupListNumber: '',
        profitName: '',
        category: 2
    });

    const fileInputRef2 = useRef(null);
    const [actionType, setActionType] = useState('delete');
    const [selectedCaseType, setSelectedCaseType] = useState(null);
    const [selectedCheckType, setSelectedCheckType] = useState(null);
    const [selectedAccountManagement, setSelectedAccountManagement] = useState(null);
    const [selectedOtherDocumentsType, setSelectedOtherDocumentsType] = useState([]);
    const [selectedSortCode, setSelectedSortCode] = useState(null);
    const [selectedSortCodeGroup, setSelectedSortCodeGroup] = useState(null);
    const { goBack } = useContext(NavHistoryContext);

    // State variables for PalCode
    const [isPropPalCodeAdding, setIsPropPalCodeAdding] = useState(false);
    const [isPropPalCodeEditing, setIsPropPalCodeEditing] = useState(false);
    const [palCode, setPalCode] = useState({
        palCodeType: '',
        palCodeId: '',
        palCodeCategory: null,
        orderNumber: '',
        code6111Id: null,
    });
    const [palCodeType, setPalCodeType] = useState('');
    const [palCodeId, setPalCodeId] = useState('');
    const [palCodeCategory, setPalCodeCategory] = useState({
        palCodeCategoryId: '',
        orderNumber: '',
        accountingGroup: null,
    });
    const [palCodeCategoryAccountingGroup, setPalCodeCategoryAccountingGroup] = useState('');
    const [editAddSelectedPalCodeCategory, setEditAddSelectedPalCodeCategory] = useState('');
    const [palCodeClassification, setPalCodeClassification] = useState('');
    const [selectedPalCode, setSelectedPalCode] = useState(null);
    const [palCodeOptions, setPalCodeOptions] = useState([]);
    const [palCodeCategoryOptions, setPalCodeCategoryOptions] = useState([]);
    const [palCodeAccountingGroupOptions, setPalCodeAccountingGroupOptions] = useState([]);
    const [code6111Options, setCode6111Options] = useState([]);
    const [filteredCode6111Options, setFilteredCode6111Options] = useState([]);
    const [filteredCode6111OptionsForSortCode, setFilteredCode6111OptionsForSortCode] = useState([]);
    const [allCode6111Options, setAllCode6111Options] = useState([]);
    const [editAddSelectedPalCodeAccountingGroupCategory, setEditAddSelectedPalCodeAccountingGroupCategory] = useState('');

    // State variables for PalCodeCategory
    const [isPropPalCodeCategoryAdding, setIsPropPalCodeCategoryAdding] = useState(false);
    const [isPropPalCodeCategoryEditing, setIsPropPalCodeCategoryEditing] = useState(false);
    const [newPalCodeCategory, setNewPalCodeCategory] = useState('');
    const [selectedPalCodeCategory, setSelectedPalCodeCategory] = useState(null);
    const [selectedPalCodeAccountingGroup, setSelectedPalCodeAccountingGroup] = useState(null);
    const [accountingGroup, setAccountingGroup] = useState({
        accountingGroupName: '',
        accountingGroupClassifaction: '',
        orderNumber: '',
    });
    const [isPropPalCodeAccountingGroupAdding, setIsPropPalCodeAccountingGroupAdding] = useState(false);
    const [isPropPalCodeAccountingGroupEditing, setIsPropPalCodeAccountingGroupEditing] = useState(false);
    const [accountingGroupClassifaction, setAccountingGroupClassifaction] = useState('');
    const [accountingGroupName, setAccountingGroupName] = useState('');
    const [orderNumber, setOrderNumber] = useState('');
    const [orderNumberCategory, setOrderNumberCategory] = useState('');
    const [openProfileModal, setOpenProfileModal] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [tableTitle, setTableTitle] = useState('');
    const [sortCodeUsageModal, setSortCodeUsageModal] = useState(false);
    const [sortCodeUsageData, setSortCodeUsageData] = useState(null);
    const handleDropdownChange = (setter) => (e) => {
        setter(e.value);
    };


    const saveSortCodeEdit = async () => {
        if (editedItem) {
            await handleSortCodeEditOptions(
                editedItem.sortCodeId,
                editedItem.sortCodeName,
                editedItem.originalSortCodeId,
                editedItem.participatesInProfitAndLoss === false ? null : editedItem.sortCodeGroup,
                editedItem.sortCodeCredit,
                editedItem.sortCodeDebit,
                parseInt(editedItem.sortCodeVatType),
                editedItem.vat,
                editedItem.participatesInProfitAndLoss,
                editedItem.pcnTransactionType || null,
                editedItem.code6111Id || null,
                editedItem.updateTransactions || false,
                editedItem.inventoryType ?? 0
            );
            setEditedItem({
                ...editedItem,
                sortCodeId: '',
                sortCodeName: '',
                originalSortCodeId: '',
                sortCodeGroup: '',
                sortCodeCredit: '',
                sortCodeDebit: '',
                sortCodeVatType: '',
                pcnTransactionType: null,
                updateTransactions: false
            });
        }
        setSelectedSortCode(null);
        setIsPropSortCodeEditing(false);
    };

    const handleSortCodeEditOptions = async (sortCodeId, sortCodeName, originalSortCodeId, sortCodeGroup, CreditAccount, DebitAccount, sortCodeVatType,vat,participatesInProfitAndLoss, pcnTransactionType, code6111Id, updateTransactions = false, inventoryType = 0) => {
        try {
            setLoading(true);
            let SortCodeResponseOk = true;
            const response = await EnumsService.UpdateSortCode(user.accountantId, originalSortCodeId, sortCodeId, sortCodeName, sortCodeGroup, CreditAccount, DebitAccount, sortCodeVatType,vat,participatesInProfitAndLoss, pcnTransactionType, code6111Id, updateTransactions, inventoryType, token);
            SortCodeResponseOk = response.ok;
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Error on save result account category data:`, errorText);
                toast.error(t('Error on save result account category data', { errorText }));
            } else {
                toast.success(t('Result account data has been saved successfully'));
            }

            if (SortCodeResponseOk) {
                await fetchEnums(token, user);
            }
        } catch (error) {
            console.error(error);
            toast.error(t('Error on save result account category data'));
        }
        setLoading(false);
    };
    // {
    //     sortCodeId: '123',
    //     sortCodeName: 'sad',
    //     sortCodeCredit: '32',
    //     sortCodeDebit: '123',
    //     sortCodeGroup: {
    //       name: 'הוצאות הפעלה ושיווק',
    //       value: 'הוצאות הפעלה ושיווק',
    //       label: 'הוצאות הפעלה ושיווק'
    //     },
    //     sortCodeVatType: { name: 'מע"מ עיסקאות', value: '1', label: 'מע"מ עיסקאות' }
    //   }
    const handlePropSortCodeSave = async () => {

        try {

            let providerResponseOk = true;
            const areSortCodeFieldsValid = areFieldsValid([sortCode.sortCodeId, sortCode.sortCodeName]);
            if (!areSortCodeFieldsValid) {
                toast.info(t('InvalidInput'));
                return
            }


            const sortCodeExists = formattedSortCodesOptions.some(option => option.sortCodeId === sortCode.sortCodeId);
            if (sortCodeExists) {
                toast.info(t('SortCodeIdAlreadyExists'));
                return;
            }


            setLoading(true);
            // Make the sortCode API call if fields are valid
            if (areSortCodeFieldsValid) {
                const response = await EnumsService.AddSortCode(user.accountantId, sortCode.sortCodeName, sortCode.sortCodeId, sortCode.participatesInProfitAndLoss === false ? null : sortCode.sortCodeGroup, sortCode.sortCodeCredit, sortCode.sortCodeDebit, parseInt(sortCode.sortCodeVatType), sortCode.vat, sortCode.participatesInProfitAndLoss, sortCode.pcnTransactionType || null, sortCode.code6111Id || null, sortCode.inventoryType ?? 0, token);
                providerResponseOk = response.ok;
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("Error adding sortCode:", errorText);
                    toast.error(t('Error on save result account category data'));
                } else {
                    toast.success(t('Result account data has been saved successfully'));
                }
            } else {
                toast.info(t('InvalidInput'));
            }

            // If the API call was successful, fetch enums
            if (providerResponseOk) {
                await fetchEnums(token, user);
            }

        } catch (error) {
            console.error(error);
            toast.error(t('Error on save result account category data'));
        }
        setIsPropSortCodeAdding(false);
        setSortCode({
            sortCodeId: '',
            sortCodeName: '',
            sortCodeCredit: '',
            sortCodeDebit: '',
            sortCodeGroup: null,
            sortCodeVatType: null,
            vat: null,
            participatesInProfitAndLoss: null,
            pcnTransactionType: null,
        });
        setLoading(false);
    };

    const handleSortCodeDeleteOptions = async (sortCodeId) => {
        try {
            setLoading(true);
            
            // First, check if sort code is being used
            const checkResponse = await EnumsService.CheckSortCodeUsage(user.accountantId, sortCodeId, token);
            
            if (checkResponse.ok) {
                const usageData = await checkResponse.json();
                
                // If sort code is being used, show modal and prevent deletion
                if (usageData.isUsed) {
                    setLoading(false);
                    
                    // Find the sort code name
                    const sortCode = sortCodesOptions.find(sc => sc.sortCodeId === sortCodeId);
                    
                    setSortCodeUsageData({
                        sortCodeId: sortCodeId,
                        sortCodeName: sortCode?.sortCodeName || sortCodeId,
                        usageDetails: usageData.usageDetails,
                        totalCount: usageData.totalCount
                    });
                    setSortCodeUsageModal(true);
                    setItemIdToDelete(null);
                    return;
                }
            }
            
            // If not used, proceed with deletion
            let SortCodeResponseOk = true;
            const response1 = await EnumsService.DeleteSortCodebyId(user.accountantId, sortCodeId, token);
            SortCodeResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error(`Error deleting sortCode:`, errorText);
                toast.error(t('providerDeleteFailure', { errorText }));
            } else {
                toast.success(t('providerDeleteSuccess'));
            }

            // If the API call was successful, fetch enums
            if (SortCodeResponseOk) {
                await fetchEnums(token, user);
            }
        } catch (error) {
            console.error(error);
            toast.error(t('ErrorCheckingSortCodeUsage'));
        }
        setItemIdToDelete(null);
        setSelectedSortCode(null);
        setLoading(false);
    };

    // Function to handle file selection
    const handleFileChange = (event, uploadFunction) => {
        const file = event.target.files[0];
        if (file) {
            uploadFunction(file); // Call the passed upload function with the selected file
        }
    };

    // Function to trigger the file input and specify the upload function
    const handleFileButtonClick = (fileInputRef, uploadFunction) => {
        // Reset the file input value
        fileInputRef.current.value = '';

        // Set up the onchange event handler
        fileInputRef.current.onchange = (event) => handleFileChange(event, uploadFunction);

        // Trigger the file input click
        fileInputRef.current.click();
    };

    // Function to upload to the second API
    const uploadFileToSortCode = async (file) => {
        setLoading(true);
        try {
            // Call the upload service method, passing the caseId, file, and auth token
            const response = await EnumsService.UploadFileData("SortCodes", user.accountantId, file, token);

            if (response.ok) {
                toast.success(t("FileUploadedSuccessfullyToSortCode"));
                // Handle success - perhaps update state or notify the user
                await fetchEnums(token, user);
            } else {
                // The server responded with an error status
                console.error("File upload failed with response status:", response.status);
                const errorText = await response.text();
                console.error("Error details:", errorText);
                // Handle failure - show error message to the user
                toast.error(t("ErrorUploadingFileToSortCode"));
            }
        } catch (error) {
            // An error occurred during the fetch operation
            console.error("Error uploading file to AccountCards API:", error.message);
            toast.error(t("ErrorUploadingFileToSortCode"));
        }
        setLoading(false);
    };

    // Function to upload to the second API
    const uploadFileToPalCode = async (file) => {
        setLoading(true);
        try {
            // Call the upload service method, passing the caseId, file, and auth token
            const response = await EnumsService.UploadFileData("PalCodes", user.accountantId, file, token);

            if (response.ok) {
                toast.success(t("FileUploadedSuccessfullyToPalCode"));
                // Handle success - perhaps update state or notify the user
                await fetchEnums(token, user);
            } else {
                // The server responded with an error status
                console.error("File upload failed with response status:", response.status);
                const errorText = await response.text();
                console.error("Error details:", errorText);
                // Handle failure - show error message to the user
                toast.error(t("ErrorUploadingFileToPalCode"));
            }
        } catch (error) {
            // An error occurred during the fetch operation
            console.error("Error uploading file to Palcode API:", error.message);
            toast.error(t("ErrorUploadingFileToPalCode"));
        }
        setLoading(false);
    };

    const uploadFileToPalCodeCategory = async (file) => {
        setLoading(true);
        try {
            // Call the upload service method, passing the caseId, file, and auth token
            const response = await EnumsService.UploadFileData("PalCodeCategories", user.accountantId, file, token);

            if (response.ok) {
                toast.success(t("FileUploadedSuccessfullyToPalCodeCategory"));
                // Handle success - perhaps update state or notify the user
                await fetchEnums(token, user);
            } else {
                // The server responded with an error status
                console.error("File upload failed with response status:", response.status);
                const errorText = await response.text();
                console.error("Error details:", errorText);
                // Handle failure - show error message to the user
                toast.error(t("ErrorUploadingFileToPalCodeCategory"));
            }
        } catch (error) {
            // An error occurred during the fetch operation
            console.error("Error uploading file to PalCodeCategory API:", error.message);
            toast.error(t("ErrorUploadingFileToPalCodeCategory"));
        }
        setLoading(false);
    };

    const uploadFileToAccountingGroup = async (file) => {
        setLoading(true);
        try {
            // Call the upload service method, passing the caseId, file, and auth token
            const response = await EnumsService.UploadFileData("PalCodeAccountingGroups", user.accountantId, file, token);

            if (response.ok) {
                toast.success(t("FileUploadedSuccessfullyToAccountingGroup"));
                // Handle success - perhaps update state or notify the user
                await fetchEnums(token, user);
            } else {
                // The server responded with an error status
                console.error("File upload failed with response status:", response.status);
                const errorText = await response.text();
                console.error("Error details:", errorText);
                // Handle failure - show error message to the user
                toast.error(t("ErrorUploadingFileToAccountingGroup"));
            }
        } catch (error) {
            // An error occurred during the fetch operation
            console.error("Error uploading file to AccountingGroup API:", error.message);
            toast.error(t("ErrorUploadingFileToAccountingGroup"));
        }
        setLoading(false);
    };
    const areFieldsValid = (fields) => {

        return fields.every(field =>
            field && (typeof field !== 'string' || field.trim() !== '')
        );
    };


    const fetchEnums = async (token, user) => {
        setLoading(true);
        try {
            // Initiate all fetch requests in parallel
            const [
                sortCodeResponse,
                sortCodeGroupResponse,
                otherDocumentsTypeResponse,
                palCodeResponse,
                palCodeCategoryResponse,
                palCodeCategoryAccountingGroupResponse
            ] = await Promise.all([
                EnumsService.GetSortCodesByAccountantId(user.accountantId, token),
                EnumsService.GetSortCodesGroupByAccountantId(user.accountantId, token),
                EnumsService.GetOtherDocumentsTypesByAccountantId(user.accountantId, token),
                EnumsService.GetPalCodesByAccountantId(user.accountantId, token),
                EnumsService.GetPalCodeCategoriesByAccountantId(user.accountantId, token),
                EnumsService.GetPalCodeAccountingGroupsByAccountantId(user.accountantId, token)
            ]);

            // Parse all JSON responses in parallel
            const [
                sortCodeData,
                sortCodeGroupData,
                otherDocumentsData,
                palCodeData,
                palCodeCategoryData,
                palCodeCategoryAccountingGroupData
            ] = await Promise.all([
                sortCodeResponse.json(),
                sortCodeGroupResponse.json(),
                otherDocumentsTypeResponse.json(),
                palCodeResponse.json(),
                palCodeCategoryResponse.json(),
                palCodeCategoryAccountingGroupResponse.json()
            ]);


            // Set state with the fetched data
            setSortCodesOptions(sortCodeData);
            setSortCodesGroupOptions(sortCodeGroupData);
            setOtherDocumentsTypeOptions(otherDocumentsData);
            setPalCodeOptions(palCodeData);
            setPalCodeCategoryOptions(palCodeCategoryData);
            setPalCodeAccountingGroupOptions(palCodeCategoryAccountingGroupData);

            // toast.success(t("Data has been fetched"));
        } catch (error) {
            console.error("Error fetching enum data:", error);
            toast.error(`${t("Error fetching enum data")}: ${error}`);
        }
        setLoading(false);
    };

    // Fetch all Code6111 options for lookup
    const fetchAllCode6111 = async () => {
        try {
            const response = await EnumsService.GetAllCode6111(token);
            if (response.ok) {
                const data = await response.json();
                setAllCode6111Options(data || []);
            } else {
                console.error("Error fetching all Code6111");
                setAllCode6111Options([]);
            }
        } catch (error) {
            console.error("Error fetching all Code6111:", error);
            setAllCode6111Options([]);
        }
    };

    // Fetch all Code6111 on component mount and when palCodeOptions change
    useEffect(() => {
        if (token && user && palCodeOptions.length > 0) {
            fetchAllCode6111();
        }
    }, [token, user, palCodeOptions.length]);

    // Fetch Code6111 options based on type
    const fetchCode6111ByType = async (type) => {
        try {
            if (!type) {
                setFilteredCode6111Options([]);
                return;
            }

            // Map PalCodeType to Code6111Type
            let code6111Type;
            if (type === "תוצאתי") {
                code6111Type = 1; // ProfitAndLoss
            } else if (type === "מאזני" || type === "מאזני רכוש קבוע") {
                code6111Type = 2; // TrialBalance
            } else {
                setFilteredCode6111Options([]);
                return;
            }

            const response = await EnumsService.GetCode6111ByType(code6111Type, token);
            if (response.ok) {
                const data = await response.json();
                setFilteredCode6111Options(data || []);
            } else {
                console.error("Error fetching Code6111 by type");
                setFilteredCode6111Options([]);
            }
        } catch (error) {
            console.error("Error fetching Code6111:", error);
            setFilteredCode6111Options([]);
        }
    };

    const fetchCode6111ByTypeForSortCode = async () => {
        try {
            const code6111Type = 1; // ProfitAndLoss
            const response = await EnumsService.GetCode6111ByType(code6111Type, token);
            if (response.ok) {
                const data = await response.json();
                setFilteredCode6111OptionsForSortCode(data || []);
            } else {
                console.error("Error fetching Code6111 for SortCode");
                setFilteredCode6111OptionsForSortCode([]);
            }
        } catch (error) {
            console.error("Error fetching Code6111 for SortCode:", error);
            setFilteredCode6111OptionsForSortCode([]);
        }
    };

    // Effect to fetch Code6111 when palCodeType changes
    useEffect(() => {
        if (palCode.palCodeType) {
            // Always fetch, even if it's the same value (handles re-selection)
            fetchCode6111ByType(palCode.palCodeType);
        } else {
            setFilteredCode6111Options([]);
        }
    }, [palCode.palCodeType]);

    // Effect to fetch Code6111 when editing
    useEffect(() => {
        if (isPropPalCodeEditing && editedItem?.palCodeType) {
            fetchCode6111ByType(editedItem.palCodeType);
        } else if (!isPropPalCodeEditing) {
            setFilteredCode6111Options([]);
        }
    }, [isPropPalCodeEditing, editedItem?.palCodeType]);

    // Effect to fetch Code6111 (type 1 - ProfitAndLoss) when SortCode modal opens
    useEffect(() => {
        if (isPropSortCodeAdding || isPropSortCodeEditing) {
            fetchCode6111ByTypeForSortCode();
        } else {
            setFilteredCode6111OptionsForSortCode([]);
        }
    }, [isPropSortCodeAdding, isPropSortCodeEditing]);

    const fetchClientsEmployeeCount = async (token, user) => {
        setLoading(true);
        try {
            // Fetching caseTypeOptions
            const ClientCountResponse = await ClientService.GetClientCounts(token, user.accountantId);
            setClientsCount(ClientCountResponse); // Transform this data if needed

            const SubAccountantCountResponse = await SubAccountantService.GetSubAccountantCounts(token, user.accountantId);
            setEmployeCount(SubAccountantCountResponse); // Transform this data if needed

        } catch (error) {
            console.error("Error fetching enum data:", error);
            toast.error(`${t("Error fetching clients and accountants data")}: ${error}`)
        }
        setLoading(false);
    };

    const handleDeleteClick = (itemId, type) => {
        setItemIdToDelete(itemId);
        setActionType(type);
        setIsModalVisible(true);
    };

    const handleConfirm = async () => {
        setIsModalVisible(false);

        if (actionType === 'SortCodeDelete') {
            await handleSortCodeDeleteOptions(itemIdToDelete);
        }
        else if (actionType === 'OtherDocumentDelete') {
            handleOtherDocumentDeleteOptions(itemIdToDelete);
        } else if (actionType === 'SortCodeGroupDelete') {
            handleSortCodeGroupDeleteOptions(itemIdToDelete);
        }
        else if (actionType === 'PalCodeDelete') {
            handlePalCodeDelete(itemIdToDelete);
        } else if (actionType === 'PalCodeCategoryDelete') {
            handlePalCodeCategoryDelete(itemIdToDelete);
        } else if (actionType === 'AccountingGroupDelete') {
            handlePalCodeAccountingGroupDelete(itemIdToDelete);
        }
    };


    const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
        <button className="ac-custom-input" onClick={onClick} ref={ref}>
            {value}
        </button>
    ));

    const init = async () => {
        setLoading(true);
        try {
            const currentUser = UserService.getCurrentUser();
            const currentToken = UserService.getCurrentToken();
            const userType = await UserService.getCurrentUserType();
            if (currentUser && currentToken) {
                setUser(currentUser);
                setToken(currentToken);
                setName(currentUser.name);
                setPhoneNumber(currentUser.phoneNumber);
                setEmail(currentUser.email);
                setPricePerHour(currentUser.pricePerHour || '');
                setMasavInstitutionSubjectCode(currentUser.masavInstitutionSubjectCode ?? '');
                setMasavSenderInstitutionCode(currentUser.masavSenderInstitutionCode ?? '');
                setMasavInstitutionName(currentUser.masavInstitutionName ?? '');
                setMasavBeneficiaryAccountType(currentUser.masavBeneficiaryAccountType ?? '');
                setMasavHeaderSerial(currentUser.masavHeaderSerial ?? '');
                setNewPassword('');
                setOldPassword('');
                await fetchEnums(currentToken, currentUser);
                await fetchClientsEmployeeCount(currentToken, currentUser);
                if (userType == 2) {
                    setIsAccountant(true)
                }
                // toast.success(t("Data has been fetched"))
            } else {
                navigate("/login"); // Redirect to login if no user or token
            }
        } catch (error) {
            console.error("Error initializing user data", error);
            toast.error(`${t("Error fetching data")}: ${error}`)
        }
        setLoading(false);
    };

    useEffect(() => {
        init();
    }, []);




    const { hasBack, handleBack } = useBack();


    const handleGoBack = () => {
        // Navigate back to the previous page using React Router
        // handleBack();
        goBack();
    };

    const handleEditProfile = () => {
        setIsEditing(true);
    };

    const validateMasavAccountantFields = () => {
        if (!isAccountant) return null;
        const tr = (s) => (s ?? '').toString().trim();
        const subj = tr(masavInstitutionSubjectCode);
        const send = tr(masavSenderInstitutionCode);
        const nm = tr(masavInstitutionName);
        const benef = tr(masavBeneficiaryAccountType);
        const ser = tr(masavHeaderSerial);
        const allEmpty = !subj && !send && !nm && !benef && !ser;
        if (allEmpty) return null;
        if (!subj || !send || !nm || !benef || !ser) return t('MasavAccountantAllOrNone');
        if (!/^\d{8}$/.test(subj)) return t('MasavValidationSubject8');
        if (!/^\d{5}$/.test(send)) return t('MasavValidationSender5');
        if (nm.length > 30) return t('MasavValidationName30');
        if (!/^\d{4}$/.test(benef)) return t('MasavValidationBenef4');
        if (!/^\d{3}$/.test(ser)) return t('MasavValidationSerial3');
        return null;
    };

    const handleSave = async (editedProfile = false) => {
        setLoading(true);
        try {
            // Optional password change: require both fields if attempting to change
            if ((newPassword && !oldPassword) || (!newPassword && oldPassword)) {
                toast.error(t("PleaseFillBothOldAndNewPasswordOrLeaveEmpty"));
                setLoading(false);
                return;
            }

            const masavErr = validateMasavAccountantFields();
            if (masavErr) {
                toast.error(masavErr);
                setLoading(false);
                return;
            }

            const editedItem = {
                AccountantId: user.accountantId, // Always include AccountantId
                ...(isAccountant ? {} : { SubAccountantId: user.subAccountantId }), // Conditionally include SubAccountantId
                PhoneNumber: phoneNumber,
                Name: name,
                Email: email,
                PricePerHour: pricePerHour ? parseFloat(pricePerHour) : null,
                ...(isAccountant
                    ? {
                          MasavInstitutionSubjectCode: masavInstitutionSubjectCode.trim() || null,
                          MasavSenderInstitutionCode: masavSenderInstitutionCode.trim() || null,
                          MasavInstitutionName: masavInstitutionName.trim() || null,
                          MasavBeneficiaryAccountType: masavBeneficiaryAccountType.trim() || null,
                          MasavHeaderSerial: masavHeaderSerial.trim() || null,
                      }
                    : {}),
                ...(newPassword && oldPassword ? { Password: newPassword, OldPassword: oldPassword } : {}),
            };
            const response = isAccountant ? await UserService.EditUser(token, editedItem) : await UserService.EditSubAccountant(token, editedItem);
            if (response.ok) {
                const response1 = isAccountant ? await UserService.UpdateUser(user.accountantId, token) : await UserService.GetSubAccountant(user.subAccountantId, token);
                if (response1) {
                    await init();
                }
                toast.success(t("Accounting data has been updated successfully"))

            } else {
                const errorText = await response.text();
                console.error(errorText);
                toast.error(`${t("Error on save new accountant data")}: ${errorText}`)
            }
        } catch (error) {
            console.error(error);
            toast.error(`${t("Error on save new accountant data")}: ${error}`)
        }
        setIsEditing(false);
        setIsChangingPassword(false);
        setLoading(false);

        if (editedProfile) {
            window.location.reload();
        }
    };

    const checkAccountantAuthCompletion = async (authWindow, accountantId, scope) => {
        setLoading(true);
        try {
            let attempts = 0;
            const maxAttempts = 25;

            while (attempts < maxAttempts) {
                // Check if the authWindow was closed by the user
                if (authWindow.closed) {
                    setLoading(false);
                    toast.error(t('Authentication window was closed by the user.'));
                    return;
                }

                // Check if authentication was completed by checking token validity
                const result = await TaxAuthorityService.getAccountantTokenDaysRemaining(accountantId, scope, token);

                if (result.daysRemaining > 0 || result.secondsRemaining > 0) {
                    setLoading(false);
                    toast.success(`${t('TokenRefreshedSuccessfully')}: ${result.daysRemaining > 0 ? result.daysRemaining + ' ' + t('Days') : result.secondsRemaining + ' ' + t('Seconds')}`);
                    setTaxAuthRefreshNonce((n) => n + 1);

                    // Close the auth window
                    if (authWindow && !authWindow.closed) {
                        authWindow.close();
                    }
                    return;
                } else {
                    attempts += 1;
                    await new Promise((resolve) => setTimeout(resolve, 3000));
                }
            }

            setLoading(false);
            toast.error(t('TokenRefreshFailed'));

            // Close the auth window on timeout
            if (authWindow && !authWindow.closed) {
                authWindow.close();
            }
        } catch (error) {
            console.error('Error checking auth completion:', error);
            setLoading(false);
            toast.error(t('TokenRefreshFailed'));

            // Close the auth window on error
            if (authWindow && !authWindow.closed) {
                authWindow.close();
            }
        }
    };

    const handleTaxAuthorityAuth = (authUrl) => {
        // Open the authentication URL in a new tab
        const authWindow = window.open(authUrl, '_blank');

        // Extract scope from the authUrl to know which scope to check
        const urlParams = new URLSearchParams(authUrl.split('?')[1]);
        const state = urlParams.get('state');
        const scopeParam = new URLSearchParams(state).get('scope');
        
        // Start checking for authentication completion
        checkAccountantAuthCompletion(authWindow, user.accountantId, scopeParam);
    };


    const savePropOtherDocumentsTypeEdit = async () => {
        if (editedItem) {
            await handlePropOtherDocumentsTypeEditSave(
                editedItem.oldOtherDocumentTypeName,
                editedItem.otherDocumentTypeName
            );
            setEditedItem({
                ...editedItem,
                oldOtherDocumentTypeName: '',
                otherDocumentTypeName: ''
            });
        }
        setIsPropOtherDocumentsTypeEditing(false);
    };

    const handlePropOtherDocumentsTypeEditSave = async (oldOtherDocumentsTypeName, otherDocumentsTypeName) => {
        setLoading(true);
        try {
            let ResponseOk = true;
            const areOkValid = areFieldsValid([user.accountantId, otherDocumentsTypeName]);

            if (areOkValid) {
                // Replace with the appropriate API call for OtherDocumentsType
                const response = await EnumsService.UpdateOtherDocumentsType(user.accountantId, otherDocumentsTypeName, oldOtherDocumentsTypeName, token);
                ResponseOk = response.ok;
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("Error updating OtherDocumentsType:", errorText);
                    toast.error(`${t("Error on save OtherDocumentsType data")}: ${errorText}`);
                }
            }

            if (ResponseOk) {
                toast.success(t("OtherDocumentsType data has been saved successfully"));
                // Fetch updated enums or perform other necessary updates
                await fetchEnums(token, user);
                setSelectedOtherDocumentsType([]);
            }

        } catch (error) {
            console.error(error);
            toast.error(t("Error on save OtherDocumentsType data"));
        }
        setLoading(false);
    };

    const savePropSortCodeGroupEdit = async () => {
        if (editedItem) {
            await handlePropSortCodeGroupEditSave(
                editedItem.editSortCodeGroupId,
                editedItem.sortCodeGroupName,
                editedItem.sortCodeGroupListNumber,
                editedItem.profitName,
                editedItem.category
            );
            setEditedItem({
                ...editedItem,
                editSortCodeGroupId: '',
                sortCodeGroupName: '',
                sortCodeGroupListNumber: '',
                profitName: ''
            });
            setSelectedSortCodeGroup(null);
        }
        setIsPropSortCodeGroupEditing(false);
    };

    const handlePropSortCodeGroupEditSave = async (Id, SortCodeGroup, SortCodeGroupListNumber, sortCodeGroupProfitName, category) => {
        setLoading(true);
        try {
            let ResponseOk = true;
            const areOkValid = areFieldsValid([user.accountantId, SortCodeGroup]);

            if (areOkValid) {
                // Replace with the appropriate API call for OtherDocumentsType
                const response = await EnumsService.UpdateSortCodeGroup(user.accountantId, SortCodeGroup, parseInt(SortCodeGroupListNumber), sortCodeGroupProfitName, Id, category, token);
                ResponseOk = response.ok;
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("Error updating SortCodeGroupEdit:", errorText);
                    toast.error(`${t("Error on save result account category data")}: ${errorText}`);
                }
            }

            if (ResponseOk) {
                toast.success(t("Result account data has been saved successfully"));
                // Fetch updated enums or perform other necessary updates
                await fetchEnums(token, user);
            }

        } catch (error) {
            console.error(error);
            toast.error(t("Error on save result account category data"));
        }
        setLoading(false);
    };


    const handlePropOtherDocumentsTypeSave = async () => {
        setLoading(true);
        try {
            let ResponseOk = true;
            const areOkValid = areFieldsValid([user.accountantId, otherDocumentsType]);

            // Make the account card API call if fields are valid
            if (areOkValid) {
                setIsPropOtherDocumentsTypeAdding(false);

                const response1 = await EnumsService.AddOtherDocumentsType(user.accountantId, otherDocumentsType, token);
                ResponseOk = response1.ok;
                if (!response1.ok) {
                    const errorText = await response1.text();
                    console.error("Error adding OtherDocuments:", errorText);
                    toast.error(`${t("Error on save OtherDocumentsType data")}: ${errorText}`);
                }
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                toast.success(t("OtherDocumentsType data has been saved successfully"));
                await fetchEnums(token, user);
            }

        } catch (error) {
            toast.error(t("Error on save OtherDocumentsType data"));
            console.error(error);
        }
        setOtherDocumentsType('');
        setLoading(false);
    };

    // {
    //     sortCodeGroupName: 'asd',
    //     sortCodeGroupListNumber: '132',
    //     profitName: 'asd',
    //     editSortCodeGroup: 'asd',
    //     sortCodeListNumber: '312',
    //     sortCodeGroupProfitName: 'asdsssss'
    //   }
    const handlePropSortCodeGroupSave = async () => {

        setLoading(true);

        try {
            let ResponseOk = true;
            const areOkValid = areFieldsValid([user.accountantId, sortCodeGroup.sortCodeGroupName, sortCodeGroup.sortCodeGroupListNumber]);

            // Make the account card API call if fields are valid
            if (areOkValid) {
                setIsPropSortCodeGroupAdding(false);

                const response1 = await EnumsService.AddSortCodeGroup(user.accountantId, sortCodeGroup.sortCodeGroupName, parseInt(sortCodeGroup.sortCodeGroupListNumber), sortCodeGroup.profitName, sortCodeGroup.category, token);
                ResponseOk = response1.ok;
                if (!response1.ok) {
                    const errorText = await response1.text();
                    console.error("Error adding SortCodeGroup:", errorText);
                    toast.error(`${t("Error on save result account category data")}: ${errorText}`);
                }
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                toast.success(t("Result account data has been saved successfully"));
                await fetchEnums(token, user);
            }

        } catch (error) {
            toast.error(t("Error on save result account category data"));
            console.error(error);
        }
        setSortCodeGroup({
            sortCodeGroupName: '',
            sortCodeGroupListNumber: '',
            profitName: '',
        })
        setLoading(false);
    };


    const handleCancel = () => {
        // Optionally reset changes
        setIsEditing(false);
        setName(user.name);
        setPhoneNumber(user.phoneNumber);
        setEmail(user.email);
        setPricePerHour(user.pricePerHour || '');
        setMasavInstitutionSubjectCode(user.masavInstitutionSubjectCode ?? '');
        setMasavSenderInstitutionCode(user.masavSenderInstitutionCode ?? '');
        setMasavInstitutionName(user.masavInstitutionName ?? '');
        setMasavBeneficiaryAccountType(user.masavBeneficiaryAccountType ?? '');
        setMasavHeaderSerial(user.masavHeaderSerial ?? '');
        setNewPassword('');
        setOldPassword('');
        setIsChangingPassword(false);
    };

    // Helper function to get translated PCN transaction type
    const getPcnTransactionTypeLabel = (pcnType, item) => {
        if (!pcnType && !item?.pcnCompositeType) return t('Default');
        const typeOrComposite = item?.pcnCompositeType === 'C+M' ? 'C+M' : pcnType;
        if (!typeOrComposite) return t('Default');
        const typeMap = {
            'S': t('RegularSale'),
            'L': t('PrivateCustomer'),
            'M': t('SelfInvoiceSale'),
            'Y': t('ExportSale'),
            'I': t('PalestinianAuthorityCustomer') || t('PalestinianCustomer'),
            'T': t('RegularInput'),
            'K': t('PettyCash'),
            'R': t('ImportInput'),
            'P': t('PalestinianSupplier'),
            'H': t('OtherDocumentByLaw') || t('OtherDocument'),
            'C': t('SelfInvoiceInput'),
            'C+M': t('CPlusM')
        };
        return typeMap[typeOrComposite] || typeOrComposite;
    };

    const formattedSortCodesOptions = sortCodesOptions?.map(option => ({

        SortingCode: option.sortCodeId,
        'SortCode Name': option.sortCodeName,
        sortCodeGroup: option.sortCodeGroup,
        'Credit Account': option.creditAccount,
        'Debit Account': option.debitAccount,
        sortCodeVatType: t(Constants.SortCodeVatType[option.sortCodeVatType]),
        vat: option.vat,
        participatesInProfitAndLoss: option.participatesInProfitAndLoss ? t("Yes") : t("No"),
        [t('PcnTransactionType')]: getPcnTransactionTypeLabel(option.pcnTransactionType),
        onEdit: () => handleEdit(option, "SortCodeEdit"),
        onDelete: () => handleDelete(option, "SortCodeDelete"),
    }));


    const formattedPalCodeOptions = palCodeOptions?.map(option => {
        // Find Code6111 info if code6111Id exists
        const code6111 = option.code6111Id 
            ? allCode6111Options.find(c => c.id === option.code6111Id)
            : null;
        console.log(code6111);
        const code6111Display = code6111 ? `${code6111.number} - ${code6111.name}` : '-';

        return {
            value: option.id,
            BalanceClassificationName: option.palCodeId,
            BalanceClassificationType: option.palCodeType,
            BalanceClassificationCategory: option.palCodeCategory,
            OrderNumber: option.orderNumber,
            Code6111: code6111Display,
            onEdit: () => handleEdit(option, "PalCodeEdit"),
            onDelete: () => handleDelete(option, "PalCodeDelete"),
        };
    });


    const formattedOtherDocumentsTypeOptions = otherDocumentsTypeOptions?.map(option => ({
        value: option.id, // Assuming each option has a unique 'id' property
        OtherDocumentsType: option.name,
        onEdit: () => handleEdit(option, "OtherDocumentEdit"), // Pass the option to your edit handler
        onDelete: () => handleDelete(option, "OtherDocumentDelete"), // Pass the option to your delete handler
    }));

    const formattedSortCodesGroupOptions = sortCodesGroupOptions?.map(option => ({
        value: option.id,
        'sortCodeGroup': option.sortCodeGroupName || option.sortCodeGroup,
        'SortCodeGroupListNumber': option.sortCodeGroupListNumber || option.sortCodeListNumber,
        'ProfitName': option.profitName || option.sortCodeGroupProfitName,
        'category': option.category,
        onEdit: () => handleEdit(option, "SortCodeGroupEdit"),
        onDelete: () => handleDelete(option, "SortCodeGroupDelete"),
    }));

    const formattedPalCodeAccountingGroupOptions = palCodeAccountingGroupOptions?.map(option => ({
        value: option.accountingGroupName,
        AccountingGroup: option.accountingGroupName,
        SerialNumber: option.orderNumber,
        Attribute: option.accountingGroupClassifaction,
        onEdit: () => handleEdit(option, "AccountingGroupEdit"),
        onDelete: () => handleDelete(option, "AccountingGroupDelete"),
    }));


    const formattedSortCodesGroupNamesOptions = sortCodesGroupOptions?.map(option => ({
        name: option.sortCodeGroup,
        value: option.sortCodeGroup,
        label: `${option.sortCodeGroup}`,

    }));
    const formattedSortCodeVatType = Object.entries(Constants.SortCodeVatType).map(([key, value]) => ({
        name: t(value),   // Using the string value (e.g., 'NotParticipating') as the name
        value: key,    // Using the number (e.g., '0') as the value
        label: t(value)   // Using the string value (e.g., 'NotParticipating') as the label
    }));

    const formattedPalCodeCategoryOptions = (palCodeCategoryOptions || []).map(option => ({
        value: option.palCodeCategoryId,
        BalanceClassificationCategory: option.palCodeCategoryId,
        SerialNumber: option.orderNumber,
        AccountingGroup: option.accountingGroup,
        onEdit: () => handleEdit(option, "PalCodeCategoryEdit"),
        onDelete: () => handleDelete(option, "PalCodeCategoryDelete"),
    }));






    const handleOtherDocumentDeleteOptions = async (otherDocumentId) => {
        setLoading(true);
        try {
            let ResponseOk = true;
            // Make the account card API call if fields are valid
            const response1 = await EnumsService.DeleteOtherDocumentbyId(otherDocumentId, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error(`Error delete otherDocumentId`, errorText);
                toast.error(`${t("Error on delete OtherDocument data")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                await fetchEnums(token, user);
                setSelectedOtherDocumentsType([]);
                toast.success(t("OtherDocument data has been deleted successfully"));
            }

        } catch (error) {
            toast.error(t("Error on delete OtherDocument data"));
            console.error(error);
        }
        setItemIdToDelete(null);
        setLoading(false);
    };

    const handleSortCodeGroupDeleteOptions = async (sortCodeGroupId) => {
        setLoading(true);
        try {
            let ResponseOk = true;
            // Make the account card API call if fields are valid
            const response1 = await EnumsService.DeleteSortCodeGroupById(user.accountantId, sortCodeGroupId, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error(`Error delete result account`, errorText);
                toast.error(`${t("Error delete result account")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                await fetchEnums(token, user);
                toast.success(t("Result account data has been deleted successfully"));
            }

        } catch (error) {
            toast.error(t("Error delete result account"));
            console.error(error);
        }
        setItemIdToDelete(null);
        setSelectedSortCodeGroup(null);
        setLoading(false);
    };

    const handlePalCodeDelete = async (palCodeId) => {
        setLoading(true);
        try {
            let ResponseOk = true;
            // Make the account card API call if fields are valid
            const response1 = await EnumsService.DeletePalCodeDelete(user.accountantId, palCodeId, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error(`BalanceClassificationNotDeletedSuccessfully`, errorText);
                toast.error(`${t("BalanceClassificationNotDeletedSuccessfully")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                await fetchEnums(token, user);
                toast.success(t("BalanceClassificationDeletedSuccessfully"));
            }

        } catch (error) {
            toast.error(t("BalanceClassificationNotDeletedSuccessfully"));
            console.error(error);
        }
        setItemIdToDelete(null);
        setSelectedPalCode(null);

        setLoading(false);
    };

    const handlePalCodeCategoryDelete = async (PalCodeCategoryId) => {
        setLoading(true);
        try {
            let ResponseOk = true;
            // Make the account card API call if fields are valid
            const response1 = await EnumsService.DeletePalCodeCategoriesDelete(user.accountantId, PalCodeCategoryId, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error(`BalanceClassificationCategoryNotDeletedSuccessfully`, errorText);
                toast.error(`${t("BalanceClassificationCategoryNotDeletedSuccessfully")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {

                toast.success(t("BalanceClassificationCategoryDeletedSuccessfully"));
            }

        } catch (error) {
            toast.error(t("BalanceClassificationCategoryNotDeletedSuccessfully"));
            console.error(error);
        }
        await fetchEnums(token, user);
        setItemIdToDelete(null);
        setSelectedPalCodeCategory(null);
        setLoading(false);
    };





    const handleSubAccountantsClick = () => {
        if (isAccountant) {
            navigate("/SubAccountants");
        }
        // You can add an else block if you want to handle the false case
    };



    const handleEdit = (item, editType) => {


        switch (editType) {
            case 'SortCodeEdit':
                setIsPropSortCodeEditing(true);
                setEditedItem({
                    ...editedItem,
                    originalSortCodeId: item.sortCodeId,
                    sortCodeId: item.sortCodeId,
                    sortCodeName: item.sortCodeName,
                    sortCodeGroup: item.sortCodeGroup,
                    sortCodeCredit: item.creditAccount,
                    sortCodeDebit: item.debitAccount,
                    sortCodeVatType: item.sortCodeVatType?.toString(),
                    vat: item.vat,
                    participatesInProfitAndLoss: item.participatesInProfitAndLoss,
                    pcnTransactionType: item.pcnTransactionType || null,
                    code6111Id: item.code6111Id || item.Code6111Id || null,
                    inventoryType: item.inventoryType ?? item.InventoryType ?? 0,
                    isEditable: item.isEditable,
                });
                break;
            case 'OtherDocumentEdit':
                setEditedItem({
                    ...editedItem,
                    oldOtherDocumentTypeName: item.name,
                    otherDocumentTypeName: item.name
                });
                setIsPropOtherDocumentsTypeEditing(true);
                break;
            case 'SortCodeGroupEdit':


                setEditedItem({
                    ...editedItem,
                    sortCodeGroupName: item.sortCodeGroup,
                    editSortCodeGroupId: item.id,
                    sortCodeGroupListNumber: item.sortCodeListNumber,
                    profitName: item.sortCodeGroupProfitName,
                    category: item.category
                });
                setIsPropSortCodeGroupEditing(true);
                break;
            case 'PalCodeEdit':
                setEditedItem({
                    ...editedItem,
                    palCodeId: item.palCodeId,
                    oldPalCodeId: item.palCodeId,
                    palCodeType: item.palCodeType,
                    palCodeCategory: item.palCodeCategory,
                    code6111Id: item.code6111Id || item.Code6111Id || null
                });
                setIsPropPalCodeEditing(true);
                break;
            case 'PalCodeCategoryEdit':
                setEditedItem({
                    ...editedItem,
                    palCodeCategoryId: item.palCodeCategoryId,
                    oldPalCodeCategoryId: item.palCodeCategoryId,
                    accountingGroup: item.accountingGroup || "",
                    orderNumber: item.orderNumber || "",
                });
                setIsPropPalCodeCategoryEditing(true);
                break;
            case 'AccountingGroupEdit':
                setEditedItem({
                    ...editedItem,
                    accountingGroupName: item.accountingGroupName,
                    oldAccountingGroupName: item.accountingGroupName,
                    accountingGroupClassifaction: item.accountingGroupClassifaction || "",
                    orderNumber: item.orderNumber || "",

                });
                setIsPropPalCodeAccountingGroupEditing(true);
                break;
            default:
                break;
        }
    };

    const handleDelete = (item, deleteType) => {

        switch (deleteType) {
            case 'SortCodeDelete':
                handleDeleteClick(item.sortCodeId, 'SortCodeDelete')
                break;
            case 'OtherDocumentDelete':
                handleDeleteClick(item.id, "OtherDocumentDelete")
                break;
            case 'SortCodeGroupDelete':
                handleDeleteClick(item.id, "SortCodeGroupDelete");
                break;
            case 'PalCodeDelete':
                handleDeleteClick(item.palCodeId, "PalCodeDelete");
                break;
            case 'PalCodeCategoryDelete':
                handleDeleteClick(item.palCodeCategoryId, "PalCodeCategoryDelete");
                break;
            case 'AccountingGroupDelete':
                handleDeleteClick(item.accountingGroupName, "AccountingGroupDelete");
                break;
            default:
                break;
        }
    };



    const handlePropPalCodeSave = async () => {
        setLoading(true);


        try {
            let ResponseOk = true;
            const newPalCode = {
                palCodeId: palCode.palCodeId,
                palCodeType: palCode.palCodeType,
                palCodeCategory: palCode.palCodeCategory,
                orderNumber: palCode.orderNumber ? parseInt(palCode.orderNumber) : null,
                code6111Id: palCode.code6111Id || null,
                accountantId: user.accountantId, // Replace with your actual accountant ID
            };
            setIsPropPalCodeAdding(false);
            // Make the account card API call if fields are valid


            const response1 = await EnumsService.AddPalCode(newPalCode, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error("BalanceClassificationNotAddedSuccessfully:", errorText);
                toast.error(`${t("BalanceClassificationNotAddedSuccessfully")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                toast.success(t("BalanceClassificationAddedSuccessfully"));

            }

        } catch (error) {
            toast.error(t("BalanceClassificationNotAddedSuccessfully"));
            console.error(error);
        }
        setPalCode({
            palCodeType: '',
            palCodeId: '',
            palCodeCategory: null,
            orderNumber: '',
            code6111Id: null,
        });
        await fetchEnums(token, user);
        setLoading(false);
    };



    const savePalCodeEdit = async () => {
        setLoading(true);
        try {
            let ResponseOk = true;
            const updatedPalCode = {
                ...editedItem,
                orderNumber: editedItem.orderNumber ? parseInt(editedItem.orderNumber) : null,
                code6111Id: editedItem.code6111Id || null,
                accountantId: user.accountantId, // Replace with your actual accountant ID
            };
            setIsPropPalCodeEditing(false);
            // Make the account card API call if fields are valid


            const response1 = await EnumsService.UpdatePalCode(updatedPalCode, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error("BalanceClassificationCategoryNotUpdatedSuccessfully:", errorText);
                toast.error(`${t("BalanceClassificationCategoryNotUpdatedSuccessfully")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                toast.success(t("BalanceClassificationUpdatedSuccessfully"));
                await fetchEnums(token, user);
            }

        } catch (error) {
            toast.error(t("BalanceClassificationNotUpdatedSuccessfully"));
            console.error(error);
        }
        setEditedItem({
            ...editedItem,
            palCodeId: '',
            oldPalCodeId: '',
            palCodeType: '',
            originalSortCodeId: '',
            palCodeClassification: '',
            orderNumber: '',
        });
        setSelectedPalCode(null);
        setLoading(false);
    };


    const handlePropPalCodeCategorySave = async () => {
        setLoading(true);

        try {
            let ResponseOk = true;
            const newPalCode = {
                PalCodeCategoryId: palCodeCategory.palCodeCategoryId,
                AccountingGroup: palCodeCategory.accountingGroup,
                accountantId: user.accountantId, // Replace with your actual accountant ID
                orderNumber: parseInt(palCodeCategory.orderNumber)
            };
            setIsPropPalCodeCategoryAdding(false);
            // Make the account card API call if fields are valid


            const response1 = await EnumsService.AddPalCodeCategory(newPalCode, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error("BalanceClassificationCategoryNotAddedSuccessfully:", errorText);
                toast.error(`${t("BalanceClassificationCategoryNotAddedSuccessfully")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                toast.success(t("BalanceClassificationCategoryAddedSuccessfully"));

            }

        } catch (error) {
            toast.error(t("BalanceClassificationCategoryNotAddedSuccessfully"));
            console.error(error);
        }
        setNewPalCodeCategory('');
        setOrderNumberCategory('');
        await fetchEnums(token, user);
        setPalCodeCategory({
            palCodeCategoryId: '',
            orderNumber: '',
            accountingGroup: null,
        })
        setLoading(false);
    };



    const savePalCodeCategoryEdit = async () => {
        setLoading(true);
        try {
            let ResponseOk = true;
            const updatedPalCodeCategory = {
                ...editedItem,
                accountantId: user.accountantId, // Replace with your actual accountant ID
            };
            setIsPropPalCodeCategoryEditing(false);
            // Make the account card API call if fields are valid


            const response1 = await EnumsService.UpdatePalCodeCategory(updatedPalCodeCategory, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error("BalanceClassificationCategoryNotUpdatedSuccessfully:", errorText);
                toast.error(`${t("BalanceClassificationCategoryNotUpdatedSuccessfully")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                toast.success(t("BalanceClassificationCategoryUpdatedSuccessfully"));
                await fetchEnums(token, user);
            }

        } catch (error) {
            toast.error(t("BalanceClassificationCategoryNotUpdatedSuccessfully"));
            console.error(error);
        }
        setEditedItem({
            ...editedItem,
            palCodeCategoryName: '',
        });
        setSelectedPalCodeCategory(null);

        setLoading(false);
    };


    const handlePropPalCodeAccountingGroupSave = async () => {
        setLoading(true);


        try {
            let ResponseOk = true;
            const newPalCodeGroupClassifaction = {
                AccountingGroupName: accountingGroup.accountingGroupName,
                AccountingGroupClassifaction: accountingGroup.accountingGroupClassifaction,
                OrderNumber: parseInt(accountingGroup.orderNumber),
                accountantId: user.accountantId, // Replace with your actual accountant ID
            };
            setIsPropPalCodeAccountingGroupAdding(false);
            // Make the account card API call if fields are valid


            const response1 = await EnumsService.AddPalCodeAccountingGroups(newPalCodeGroupClassifaction, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error("AccountingGroupNotAddedSuccessfully:", errorText);
                toast.error(`${t("AccountingGroupNotAddedSuccessfully")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                toast.success(t("AccountingGroupAddedSuccessfully"));

            }

        } catch (error) {
            toast.error(t("AccountingGroupNotAddedSuccessfully"));
            console.error(error);
        }
        setAccountingGroupName('');
        setOrderNumber('');
        setAccountingGroupClassifaction('');
        await fetchEnums(token, user);
        setLoading(false);
    };



    const savePalCodeAccountingGroupEdit = async () => {
        setLoading(true);
        try {
            let ResponseOk = true;
            const updatedPalCodeAccountingGroups = {
                ...editedItem,
                accountantId: user.accountantId, // Replace with your actual accountant ID
            };
            setIsPropPalCodeAccountingGroupEditing(false);
            // Make the account card API call if fields are valid


            const response1 = await EnumsService.UpdatePalCodeAccountingGroups(updatedPalCodeAccountingGroups, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error("AccountingGroupNotUpdatedSuccessfully:", errorText);
                toast.error(`${t("AccountingGroupNotUpdatedSuccessfully")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {
                toast.success(t("AccountingGroupUpdatedSuccessfully"));
                await fetchEnums(token, user);
            }

        } catch (error) {
            toast.error(t("AccountingGroupNotUpdatedSuccessfully"));
            console.error(error);
        }
        setEditedItem({
            ...editedItem,
            accountingGroupName: "",
            oldAccountingGroupName: "",
            accountingGroupClassifaction: "",
            orderNumber: "",

        });
        setSelectedPalCodeAccountingGroup(null);
        setLoading(false);
    };

    const handlePalCodeAccountingGroupDelete = async (AccountingGroupName) => {
        setLoading(true);
        try {
            let ResponseOk = true;
            // Make the account card API call if fields are valid
            const response1 = await EnumsService.DeletePalCodeAccountingGroups(user.accountantId, AccountingGroupName, token);
            ResponseOk = response1.ok;
            if (!response1.ok) {
                const errorText = await response1.text();
                console.error(`AccountingGroupNotDeletedSuccessfully`, errorText);
                toast.error(`${t("AccountingGroupNotDeletedSuccessfully")}: ${errorText}`);
            }

            // If any API call was successful, fetch enums
            if (ResponseOk) {

                toast.success(t("AccountingGroupDeletedSuccessfully"));
            }

        } catch (error) {
            toast.error(t("AccountingGroupNotDeletedSuccessfully"));
            console.error(error);
        }
        await fetchEnums(token, user);
        setItemIdToDelete(null);
        setSelectedPalCodeAccountingGroup(null);
        setLoading(false);
    };




    const handleViewTable = (data, title) => {
        // This could be the entire array
        setTableData(data);
        setTableTitle(title);
        setOpenDialog(true);
    };

    const handleMergeSortCodes = async (primarySortCodeId, secondarySortCodeId) => {
        try {
            setLoading(true);
            
            const response = await EnumsService.MergeSortCodes(
                user.accountantId,
                primarySortCodeId,
                secondarySortCodeId,
                token
            );
            
            if (response.ok) {
                toast.success(t('MergeSuccessful'));
                // Refresh the sort codes data
                await fetchEnums(token, user);
                setOpenDialog(false); // Close the dialog after successful merge
            } else {
                const errorMessage = await response.text();
                console.error('Merge error:', errorMessage);
                toast.error(t('FailedToMerge'));
            }
        } catch (error) {
            console.error('Error merging sort codes:', error);
            toast.error(t('FailedToMerge'));
        } finally {
            setLoading(false);
        }
    };

    const handleBatchMergeSortCodes = async (batchMergeData) => {
        try {
            setLoading(true);

            const response = await EnumsService.BatchMergeSortCodes(
                user.accountantId,
                batchMergeData.mergeOperations,
                batchMergeData.deleteOldCodes,
                token
            );

            if (response.ok) {
                const result = await response.json();
                await fetchEnums(token, user);

                const Swal = require('sweetalert2');
                Swal.fire({
                    title: t('MergeResults'),
                    html: `
                        <div style="direction: rtl; text-align: right; max-height: 70vh; overflow-y: auto;">
                            <div style="margin-bottom: 10px;">
                                <div style="padding: 8px; background-color: #d4edda; border-radius: 6px; margin-bottom: 6px;">
                                    <strong style="color: #155724; font-size: 13px;">✓ ${t('SuccessfulOperations')}: ${result.successCount || 0}</strong>
                                </div>
                                ${result.failureCount > 0 ? `
                                    <div style="padding: 8px; background-color: #f8d7da; border-radius: 6px;">
                                        <strong style="color: #721c24; font-size: 13px;">✗ ${t('FailedOperations')}: ${result.failureCount}</strong>
                                    </div>
                                ` : ''}
                            </div>
                            <div style="max-height: 50vh; overflow-y: auto;">
                                ${(result.results || []).map((res, index) => `
                                    <div style="padding: 8px; margin: 6px 0; border: 1px solid #ddd; border-radius: 6px; background: ${res.success ? '#f8fff8' : '#fff8f8'}; text-align: right; direction: rtl;">
                                        <strong style="font-size: 12px;">${t('Operation')} ${index + 1}: ${res.baseCardName || res.baseCardId}</strong>
                                        ${res.mergedCards ? `
                                            <ul style="margin: 5px 0; padding-right: 20px; padding-left: 0; font-size: 11px; list-style-position: inside;">
                                                ${res.mergedCards.map(card => `
                                                    <li style="color: ${card.success ? 'green' : 'red'}; text-align: right;">
                                                        <strong>${card.cardName || card.cardId}</strong> (${card.cardId}): ${card.message === 'MergedSuccessfully' ? t('MergedSuccessfully') : card.message}
                                                    </li>
                                                `).join('')}
                                            </ul>
                                        ` : `<p style="color: red; font-size: 11px; text-align: right;">${res.message || ''}</p>`}
                                    </div>
                                `).join('')}
                            </div>
                        </div>
                    `,
                    icon: result.failureCount === 0 ? 'success' : 'warning',
                    confirmButtonText: t('Close'),
                    width: '90%',
                    maxWidth: '600px',
                    customClass: { popup: 'swal2-rtl', htmlContainer: 'swal2-rtl' }
                });

                setOpenDialog(false);
                return true;
            } else {
                const errorMessage = await response.text();
                console.error('Batch merge error:', errorMessage);
                toast.error(t('FailedToBatchMerge') || t('FailedToMerge'));
                return false;
            }
        } catch (error) {
            console.error('Error batch merging sort codes:', error);
            toast.error(t('FailedToBatchMerge') || t('FailedToMerge'));
            return false;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!openDialog) return; 

        let newFormattedData = [];

        switch (tableTitle) {
            case "SortingCode":
                newFormattedData = sortCodesOptions?.map(option => {
                    // Find Code6111 info if code6111Id exists
                    const code6111 = option.code6111Id 
                        ? allCode6111Options.find(c => c.id === option.code6111Id)
                        : null;
                    const code6111Display = code6111 ? `${code6111.number} - ${code6111.name}` : '';

                    return {
                        // Keep original properties for table display
                        SortingCode: option.sortCodeId,
                        'SortCode Name': option.sortCodeName,
                        sortCodeGroup: option.sortCodeGroup,
                        'Credit Account': option.creditAccount,
                        'Debit Account': option.debitAccount,
                        sortCodeVatType: t(Constants.SortCodeVatType[option.sortCodeVatType]),
                        vat: option.vat,
                        participatesInProfitAndLoss: option.participatesInProfitAndLoss ? t("Yes") : t("No"),
                        [t('PcnTransactionType')]: getPcnTransactionTypeLabel(option.pcnTransactionType),
                        Code6111: code6111Display,
                        
                        // Add properties needed for MergeSortCodes component
                        sortCodeId: option.sortCodeId,
                        sortCodeName: option.sortCodeName,
                        isEditable: option.isEditable, // Add isEditable field

                        // Only show edit/delete if isEditable is true
                        onEdit: option.isEditable ? () => handleEdit(option, "SortCodeEdit") : null,
                        onDelete: option.isEditable ? () => handleDelete(option, "SortCodeDelete") : null,
                    };
                });
                newFormattedData = sortCodesOptions?.map(option => ({
                    // Keep original properties for table display
                    SortingCode: option.sortCodeId,
                    'SortCode Name': option.sortCodeName,
                    sortCodeGroup: option.sortCodeGroup,
                    'Credit Account': option.creditAccount,
                    'Debit Account': option.debitAccount,
                    sortCodeVatType: t(Constants.SortCodeVatType[option.sortCodeVatType]),
                    vat: option.vat,
                    participatesInProfitAndLoss: option.participatesInProfitAndLoss ? t("Yes") : t("No"),
                    [t('PcnTransactionType')]: getPcnTransactionTypeLabel(option.pcnTransactionType),
                    
                    // Add properties needed for MergeSortCodes component
                    sortCodeId: option.sortCodeId,
                    sortCodeName: option.sortCodeName,
                    isEditable: option.isEditable,

                    onEdit: () => handleEdit(option, "SortCodeEdit"),
                    onDelete: option.isEditable ? () => handleDelete(option, "SortCodeDelete") : null,
                }));
                break;

            case "PalCode":
                newFormattedData = palCodeOptions?.map(option => {
                    // Find Code6111 info if code6111Id exists
                    const code6111 = option.code6111Id 
                        ? allCode6111Options.find(c => c.id === option.code6111Id)
                        : null;
                    const code6111Display = code6111 ? `${code6111.number} - ${code6111.name}` : '';

                    return {
                        value: option.id,
                        BalanceClassificationName: option.palCodeId,
                        BalanceClassificationType: option.palCodeType,
                        BalanceClassificationCategory: option.palCodeCategory,
                        OrderNumber: option.orderNumber,
                        Code6111: code6111Display,
                        onEdit: () => handleEdit(option, "PalCodeEdit"),
                        onDelete: () => handleDelete(option, "PalCodeDelete"),
                    };
                });
                break;


                break;

            case "sortCodeGroup":
                newFormattedData = sortCodesGroupOptions?.map(option => ({
                    value: option.id,
                    'sortCodeGroup': option.sortCodeGroupName || option.sortCodeGroup,
                    'SortCodeGroupListNumber': option.sortCodeGroupListNumber || option.sortCodeListNumber,
                    'ProfitName': option.profitName || option.sortCodeGroupProfitName,
                    groupType: Constants.SortCodeGroupCategoryName?.[option.category] || "",
                    onEdit: () => handleEdit(option, "SortCodeGroupEdit"),
                    onDelete: () => handleDelete(option, "SortCodeGroupDelete"),
                }));
                break;

            case "AccountingGroup":
                newFormattedData = palCodeAccountingGroupOptions?.map(option => ({
                    value: option.accountingGroupName,
                    AccountingGroup: option.accountingGroupName,
                    SerialNumber: option.orderNumber,
                    Attribute: option.accountingGroupClassifaction,
                    onEdit: () => handleEdit(option, "AccountingGroupEdit"),
                    onDelete: () => handleDelete(option, "AccountingGroupDelete"),
                }));
                break;

            case "BalanceClassificationCategory":
                newFormattedData = palCodeCategoryOptions?.map(option => ({
                    value: option.palCodeCategoryId,
                    BalanceClassificationCategory: option.palCodeCategoryId,
                    SerialNumber: option.orderNumber,
                    AccountingGroup: option.accountingGroup,
                    onEdit: () => handleEdit(option, "PalCodeCategoryEdit"),
                    onDelete: () => handleDelete(option, "PalCodeCategoryDelete"),
                }));
                break;

            default:
                newFormattedData = [];
                break;
        }

        setTableData(newFormattedData);

    }, [
        openDialog,
        tableTitle,
        sortCodesOptions,
        palCodeOptions,
        otherDocumentsTypeOptions,
        sortCodesGroupOptions,
        palCodeAccountingGroupOptions,
        palCodeCategoryOptions
    ]);
    return (
        <>

            <ProfilePictureDialog
                open={openProfileModal}
                onClose={() => setOpenProfileModal(false)}
                existingProfilePicture={user?.profilePicture}   // if available
                token={token}
                accountantId={user?.accountantId}
                saveFunc={handleSave}
            />
            <DataTableDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                data={tableData}
                showMergeButton={tableTitle === "SortingCode"}
                title={tableTitle}
                onMerge={handleMergeSortCodes}
                onBatchMerge={handleBatchMergeSortCodes}
            />
            <div className={styles.page} dir={direction === 'rtl' ? 'rtl' : 'ltr'}>

                    <div className={styles.header} style={{ flexDirection: direction === 'rtl' ? 'row' : 'row' }}>
                        <h1 className={`${styles.title} ${styles[direction] || ''}`}>{t('AccountantDetails')}</h1>

                        <div className={styles.backBtn} onClick={handleGoBack} role="button" tabIndex={0} aria-label={t('back')}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 32 32" fill="none">
                                <path d="M20 21.1667L14.8267 15.9933L20 10.82C20.52 10.3 20.52 9.45999 20 8.93999C19.48 8.41999 18.64 8.41999 18.12 8.93999L12 15.06C11.48 15.58 11.48 16.42 12 16.94L18.12 23.06C18.64 23.58 19.48 23.58 20 23.06C20.5067 22.54 20.52 21.6867 20 21.1667Z" fill="black" fillOpacity="0.9" />
                            </svg>
                        </div>
                    </div>

                    <div className={styles.statsRow} style={{ justifyContent: direction === 'rtl' ? 'flex-start' : 'flex-end' }}>
                        <div className={styles.statChip}>
                            <div className={styles.statIcon}>
                                <img src={clientsIcon} alt="" />
                            </div>
                            <div className={styles.statMeta}>
                                <div className={`${styles.statLabel} ${styles[direction] || ''}`}>{t('clients')}</div>
                                <div className={`${styles.statValue} ${styles[direction] || ''}`}>{beautifyNumber(clientsCount)}</div>
                            </div>
                        </div>
                        <div className={styles.statChip}>
                            <div className={styles.statIcon}>
                                <img src={subAccountantsIcon} alt="" />
                            </div>
                            <div className={styles.statMeta}>
                                <div className={`${styles.statLabel} ${styles[direction] || ''}`}>{t('SubAccountants')}</div>
                                <div className={`${styles.statValue} ${styles[direction] || ''}`}>{beautifyNumber(employeCount)}</div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.profileToolbar}>
                        <div className={styles.profileToolbarLeft}>
                            <h2 className={styles.profileHeading}>{t('profile')}</h2>
                            <button
                                type="button"
                                className={styles.btnPrimary}
                                onClick={() => setOpenProfileModal(true)}
                            >
                                {t('AddOrUpdateProfilePicture')}
                            </button>
                        </div>
                        <div className={styles.profileActions}>
                            {!isEditing ? (
                                <IconButton className={styles.iconBtn} onClick={handleEditProfile}>
                                    <img src={editIconImage} alt="Edit" />
                                </IconButton>
                            ) : (
                                <IconButton className={styles.iconBtnWarn} onClick={handleCancel}>
                                    <Cancel sx={{ color: '#E11D48', width: '22px', height: '22px' }} />
                                </IconButton>
                            )}
                        </div>
                    </div>

                    <div className={isAccountant ? styles.profileGridSplit : styles.profileGrid}>
                        {/* Contact details card */}
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.cardHeaderTitle}>
                                    <span className={styles.cardIcon}><PersonOutlineIcon /></span>
                                    <h3 className={styles.cardTitle}>{t('ContactDetails')}</h3>
                                </div>
                            </div>

                            <div className={styles.fieldsGridCols3} dir={direction === 'rtl' ? 'rtl' : 'ltr'}>
                                <div className={styles.field}>
                                    <div className={styles.fieldLabel}>{t('accountantName')}</div>
                                    <TextField
                                        fullWidth size="small" placeholder={t('accountantName')}
                                        value={name} onChange={(e) => setName(e.target.value)}
                                        variant="standard" disabled={!isEditing}
                                        InputProps={{
                                            disableUnderline: true,
                                            className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                            sx: profileCompactInputSx,
                                        }}
                                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <div className={styles.fieldLabel}>{t('AccountantId')}</div>
                                    <TextField
                                        fullWidth size="small" placeholder={t('AccountantId')}
                                        value={isAccountant ? user.accountantId : user.subAccountantId}
                                        variant="standard" disabled
                                        InputProps={{
                                            disableUnderline: true,
                                            className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                            sx: profileCompactInputSx,
                                        }}
                                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <div className={styles.fieldLabel}>{t('phoneNumber')}</div>
                                    <TextField
                                        fullWidth size="small" placeholder={t('phoneNumber')}
                                        value={phoneNumber} type="number"
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        variant="standard" disabled={!isEditing}
                                        InputProps={{
                                            disableUnderline: true,
                                            className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                            sx: profileCompactInputSx,
                                        }}
                                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <div className={styles.fieldLabel}>{t('email')}</div>
                                    <TextField
                                        fullWidth size="small" placeholder={t('email')}
                                        value={email} onChange={(e) => setEmail(e.target.value)}
                                        variant="standard" disabled={!isEditing}
                                        InputProps={{
                                            disableUnderline: true,
                                            className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                            sx: profileCompactInputSx,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <MailOutlineIcon sx={{ color: '#2563EB', fontSize: 18 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <div className={styles.fieldLabel}>{t('HourlyCost') || 'Hourly Cost (₪)'}</div>
                                    <TextField
                                        fullWidth size="small"
                                        placeholder={t('enterHourlyCost') || 'Enter hourly cost'}
                                        value={pricePerHour} type="number"
                                        onChange={(e) => setPricePerHour(e.target.value)}
                                        variant="standard" disabled={!isEditing}
                                        InputProps={{
                                            disableUnderline: true,
                                            className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                            sx: {
                                                ...profileCompactInputSx,
                                                '& .MuiInputAdornment-root': { marginInlineEnd: '6px' },
                                            },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <span style={{ color: '#666', fontSize: '14px' }}>₪</span>
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{
                                            style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                                            min: 0,
                                            step: 0.01,
                                        }}
                                    />
                                </div>
                            </div>

                            <div className={styles.cardFooter} style={{ justifyContent: direction === 'rtl' ? 'flex-start' : 'flex-end' }}>
                                {isEditing && isChangingPassword ? (
                                    <div style={{ width: '100%' }}>
                                        <div className={styles.cardTitle} style={{ fontSize: 14, marginBottom: 10 }}>
                                            {t('ChangePassword')}
                                        </div>
                                        <div className={styles.passwordFields} dir={direction === 'rtl' ? 'rtl' : 'ltr'}>
                                            <div className={styles.field}>
                                                <div className={styles.fieldLabel}>{t('enterOldPassword')}</div>
                                                <TextField
                                                    fullWidth size="small" type="password"
                                                    placeholder={t('enterOldPassword')}
                                                    value={oldPassword} onChange={(e) => setOldPassword(e.target.value)}
                                                    variant="standard" disabled={!isEditing}
                                                    InputProps={{
                                                        disableUnderline: true,
                                                        className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                                        sx: profileCompactInputSx,
                                                    }}
                                                    inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                                />
                                            </div>
                                            <div className={styles.field}>
                                                <div className={styles.fieldLabel}>{t('enterPassword')}</div>
                                                <TextField
                                                    fullWidth size="small" type="password"
                                                    placeholder={t('enterPassword')}
                                                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                                    variant="standard" disabled={!isEditing}
                                                    InputProps={{
                                                        disableUnderline: true,
                                                        className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                                        sx: profileCompactInputSx,
                                                    }}
                                                    inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.btnOutline}
                                            onClick={() => {
                                                setIsChangingPassword(false);
                                                setOldPassword('');
                                                setNewPassword('');
                                            }}
                                        >
                                            {t('Cancel')}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className={styles.btnPrimary}
                                        disabled={!isEditing}
                                        onClick={() => { if (isEditing) setIsChangingPassword(true); }}
                                    >
                                        <LockOutlinedIcon />
                                        {t('ChangePassword')}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* MASAV card (accountants only) */}
                        {isAccountant && (
                            <div className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <div className={styles.cardHeaderTitle}>
                                        <span className={styles.cardIcon}><SettingsOutlinedIcon /></span>
                                        <h3 className={styles.cardTitle}>{t('MasavAccountantSettingsSection')}</h3>
                                    </div>
                                    {!isEditing ? (
                                        <button type="button" className={styles.btnOutline} onClick={handleEditProfile}>
                                            <img src={editIconImage} alt="" width={14} height={14} />
                                            {t('EditDetails', { defaultValue: 'עריכת פרטים' })}
                                        </button>
                                    ) : null}
                                </div>

                                <div className={styles.fieldsGridCols3} dir={direction === 'rtl' ? 'rtl' : 'ltr'}>
                                    <div className={styles.field}>
                                        <div className={styles.fieldLabel}>{t('MasavInstitutionSubjectCodeLabel')}</div>
                                        <TextField
                                            fullWidth size="small" placeholder="12345678"
                                            value={masavInstitutionSubjectCode}
                                            onChange={(e) => setMasavInstitutionSubjectCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                                            variant="standard" disabled={!isEditing}
                                            InputProps={{
                                                disableUnderline: true,
                                                className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                                sx: profileCompactInputSx,
                                            }}
                                            inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' }, maxLength: 8, inputMode: 'numeric' }}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.fieldLabel}>{t('MasavSenderInstitutionCodeLabel')}</div>
                                        <TextField
                                            fullWidth size="small" placeholder="12345"
                                            value={masavSenderInstitutionCode}
                                            onChange={(e) => setMasavSenderInstitutionCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                                            variant="standard" disabled={!isEditing}
                                            InputProps={{
                                                disableUnderline: true,
                                                className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                                sx: profileCompactInputSx,
                                            }}
                                            inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' }, maxLength: 5, inputMode: 'numeric' }}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.fieldLabel}>{t('MasavInstitutionNameMasavLabel')}</div>
                                        <TextField
                                            fullWidth size="small"
                                            value={masavInstitutionName}
                                            onChange={(e) => setMasavInstitutionName(e.target.value.slice(0, 30))}
                                            variant="standard" disabled={!isEditing}
                                            InputProps={{
                                                disableUnderline: true,
                                                className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                                sx: profileCompactInputSx,
                                            }}
                                            inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' }, maxLength: 30 }}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.fieldLabel}>{t('MasavBeneficiaryAccountTypeLabel')}</div>
                                        <TextField
                                            fullWidth size="small" placeholder="0000"
                                            value={masavBeneficiaryAccountType}
                                            onChange={(e) => setMasavBeneficiaryAccountType(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                            variant="standard" disabled={!isEditing}
                                            InputProps={{
                                                disableUnderline: true,
                                                className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                                sx: profileCompactInputSx,
                                            }}
                                            inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' }, maxLength: 4, inputMode: 'numeric' }}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.fieldLabel}>{t('MasavHeaderSerialLabel')}</div>
                                        <TextField
                                            fullWidth size="small" placeholder="001"
                                            value={masavHeaderSerial}
                                            onChange={(e) => setMasavHeaderSerial(e.target.value.replace(/\D/g, '').slice(0, 3))}
                                            variant="standard" disabled={!isEditing}
                                            InputProps={{
                                                disableUnderline: true,
                                                className: `accountantDetailsCompactInput custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                                sx: profileCompactInputSx,
                                            }}
                                            inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' }, maxLength: 3, inputMode: 'numeric' }}
                                        />
                                    </div>
                                </div>

                                {isEditing && (
                                    <div className={styles.cardFooter} style={{ justifyContent: 'flex-end' }}>
                                        <button type="button" className={styles.btnSave} onClick={handleSave}>
                                            {t('save')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {isEditing && !isAccountant && (
                        <div className={styles.saveRow} style={{ justifyContent: direction === 'rtl' ? 'flex-start' : 'flex-end' }}>
                            <button type="button" className={styles.btnSave} onClick={handleSave}>
                                {t('save')}
                            </button>
                        </div>
                    )}

                    {isAccountant && (
                        <AccountantTaxAuthoritySection
                            accountant={user}
                            token={token}
                            handleTaxAuthorityAuth={handleTaxAuthorityAuth}
                            setLoading={setLoading}
                            refreshNonce={taxAuthRefreshNonce}
                        />
                    )}

                    <div className={styles.propertiesGrid}>
                        <div className={styles.propertyCard}>
                            <div className={styles.propertyHeader}>
                                <div className={styles.propertyTitleWrap}>
                                    <span className={styles.cardIcon}><AccountTreeOutlinedIcon /></span>
                                    <h3 className={styles.propertyTitle}>{t('AccountingGroup')}</h3>
                                </div>
                                <div className={styles.propertyActions}>
                                    <input type="file" ref={fileInputRef2} style={{ display: 'none' }} />
                                    <IconButton className={styles.propertyActionBtn} onClick={() => setIsPropPalCodeAccountingGroupAdding(true)}>
                                        <img src={addIconImage} alt="Add" />
                                    </IconButton>
                                    <IconButton className={styles.propertyActionBtnAlt} onClick={() => handleFileButtonClick(fileInputRef2, uploadFileToAccountingGroup)}>
                                        <img src={importIconImage} alt="Import" />
                                    </IconButton>
                                    <Tooltip title={t('DownloadTemplate')} arrow>
                                        <IconButton
                                            className={styles.propertyActionBtn}
                                            onClick={() => handleDownloadTemplate(accountingGroupFile, "Xtram_AccountingGroup_Template.xlsx")}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                            <p className={styles.propertyHint}>{t('SelectAccountingGroup')}</p>
                            {!isPropPalCodeAccountingGroupAdding && !isPropPalCodeAccountingGroupEditing ? (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    endIcon={<TableViewIcon />}
                                    className={styles.viewAllBtn}
                                    onClick={() => handleViewTable(formattedPalCodeAccountingGroupOptions, 'AccountingGroup')}
                                >
                                    {t("ViewAllAccountingGroups")}
                                </Button>
                            ) : (
                                <AccountantDetailsModals
                                    isPopupOpen={isPropPalCodeAccountingGroupAdding || isPropPalCodeAccountingGroupEditing}
                                    value={isPropPalCodeAccountingGroupAdding ? accountingGroup : editedItem}
                                    handleValueChange={isPropPalCodeAccountingGroupAdding ? setAccountingGroup : setEditedItem}
                                    isEditMode={isPropPalCodeAccountingGroupEditing}
                                    handleSaveChange={isPropPalCodeAccountingGroupAdding ? handlePropPalCodeAccountingGroupSave : savePalCodeAccountingGroupEdit}
                                    handleClose={() => {
                                        setIsPropPalCodeAccountingGroupAdding(false);
                                        setIsPropPalCodeAccountingGroupEditing(false);
                                    }}
                                    contentKey={"AccountingGroup"}
                                    Constants={Constants}
                                    customStyles={customStyles}
                                    t={t}
                                    direction={direction}
                                    existingAccountingGroups={palCodeAccountingGroupOptions}
                                />
                            )}
                        </div>

                        <div className={styles.propertyCard}>
                            <div className={styles.propertyHeader}>
                                <div className={styles.propertyTitleWrap}>
                                    <span className={styles.cardIcon}><CategoryOutlinedIcon /></span>
                                    <h3 className={styles.propertyTitle}>{t('sortCodeGroup')}</h3>
                                </div>
                                <div className={styles.propertyActions}>
                                    <IconButton className={styles.propertyActionBtn} onClick={() => setIsPropSortCodeGroupAdding(true)}>
                                        <img src={addIconImage} alt="Add" />
                                    </IconButton>
                                </div>
                            </div>
                            <p className={styles.propertyHint}>{t('SelectSortCodeGroupName')}</p>
                            {!isPropSortCodeGroupAdding && !isPropSortCodeGroupEditing ? (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    endIcon={<TableViewIcon />}
                                    className={styles.viewAllBtn}
                                    onClick={() => handleViewTable(formattedSortCodesGroupOptions, 'sortCodeGroup')}
                                >
                                    {t("ViewAllSortCodeGroup")}
                                </Button>
                            ) : (
                                <AccountantDetailsModals
                                    isPopupOpen={isPropSortCodeGroupAdding || isPropSortCodeGroupEditing}
                                    value={isPropSortCodeGroupAdding ? sortCodeGroup : editedItem}
                                    handleValueChange={isPropSortCodeGroupAdding ? setSortCodeGroup : setEditedItem}
                                    isEditMode={isPropSortCodeGroupEditing}
                                    handleSaveChange={isPropSortCodeGroupAdding ? handlePropSortCodeGroupSave : savePropSortCodeGroupEdit}
                                    handleClose={() => {
                                        setIsPropSortCodeGroupAdding(false);
                                        setIsPropSortCodeGroupEditing(false);
                                    }}
                                    contentKey={"SortingCodeGroup"}
                                    existingSortCodeGroups={sortCodesGroupOptions}
                                />
                            )}
                        </div>

                        <div className={styles.propertyCard}>
                            <div className={styles.propertyHeader}>
                                <div className={styles.propertyTitleWrap}>
                                    <span className={styles.cardIcon}><FolderOpenOutlinedIcon /></span>
                                    <h3 className={styles.propertyTitle}>{t('BalanceClassificationCategory')}</h3>
                                </div>
                                <div className={styles.propertyActions}>
                                    <input type="file" ref={fileInputRef2} style={{ display: 'none' }} />
                                    <IconButton className={styles.propertyActionBtn} onClick={() => setIsPropPalCodeCategoryAdding(true)}>
                                        <img src={addIconImage} alt="Add" />
                                    </IconButton>
                                    <IconButton className={styles.propertyActionBtnAlt} onClick={() => handleFileButtonClick(fileInputRef2, uploadFileToPalCodeCategory)}>
                                        <img src={importIconImage} alt="Import" />
                                    </IconButton>
                                    <Tooltip title={t('DownloadTemplate')} arrow>
                                        <IconButton
                                            className={styles.propertyActionBtn}
                                            onClick={() => handleDownloadTemplate(palCodeCategoryFile, "Xtram_PalCodeCategory_Template.xlsx")}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                            <p className={styles.propertyHint}>{t('SelectBalanceClassificationCategory')}</p>
                            {!isPropPalCodeCategoryAdding && !isPropPalCodeCategoryEditing ? (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    endIcon={<TableViewIcon />}
                                    className={styles.viewAllBtn}
                                    onClick={() => handleViewTable(formattedPalCodeCategoryOptions, 'BalanceClassificationCategory')}
                                >
                                    {t("ViewAllBalanceClassificationCategory")}
                                </Button>
                            ) : (
                                <AccountantDetailsModals
                                    isPopupOpen={isPropPalCodeCategoryAdding || isPropPalCodeCategoryEditing}
                                    value={isPropPalCodeCategoryAdding ? palCodeCategory : editedItem}
                                    handleValueChange={isPropPalCodeCategoryAdding ? setPalCodeCategory : setEditedItem}
                                    isEditMode={isPropPalCodeCategoryEditing}
                                    handleSaveChange={isPropPalCodeCategoryAdding ? handlePropPalCodeCategorySave : savePalCodeCategoryEdit}
                                    handleClose={() => {
                                        setIsPropPalCodeCategoryAdding(false);
                                        setIsPropPalCodeCategoryEditing(false);
                                    }}
                                    contentKey={"PalCodeCategory"}
                                    formattedPalCodeAccountingGroupOptions={palCodeAccountingGroupOptions}
                                    customStyles={customStyles}
                                    t={t}
                                    direction={direction}
                                    existingPalCodeCategories={palCodeCategoryOptions}
                                />
                            )}
                        </div>

                        <div className={styles.propertyCard}>
                            <div className={styles.propertyHeader}>
                                <div className={styles.propertyTitleWrap}>
                                    <span className={styles.cardIcon}><SellOutlinedIcon /></span>
                                    <h3 className={styles.propertyTitle}>{t('SortingCode')}</h3>
                                </div>
                                <div className={styles.propertyActions}>
                                    <input type="file" ref={fileInputRef2} style={{ display: 'none' }} />
                                    <IconButton className={styles.propertyActionBtn} onClick={() => setIsPropSortCodeAdding(true)}>
                                        <img src={addIconImage} alt="Add" />
                                    </IconButton>
                                    <IconButton className={styles.propertyActionBtnAlt} onClick={() => handleFileButtonClick(fileInputRef2, uploadFileToSortCode)}>
                                        <img src={importIconImage} alt="Import" />
                                    </IconButton>
                                    <Tooltip title={t('DownloadTemplate')} arrow>
                                        <IconButton
                                            className={styles.propertyActionBtn}
                                            onClick={() => handleDownloadTemplate(sortCodesFile, "Xtram_SortCodes_template.xlsx")}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                            <p className={styles.propertyHint}>{t('Select SortCode')}</p>
                            {!isPropSortCodeAdding && !isPropSortCodeEditing ? (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    endIcon={<TableViewIcon />}
                                    className={styles.viewAllBtn}
                                    onClick={() => handleViewTable(formattedSortCodesOptions, 'SortingCode')}
                                >
                                    {t("ViewAllSortingCode")}
                                </Button>
                            ) : (
                                <AccountantDetailsModals
                                    isPopupOpen={isPropSortCodeAdding || isPropSortCodeEditing}
                                    value={isPropSortCodeAdding ? sortCode : editedItem}
                                    handleValueChange={isPropSortCodeAdding ? setSortCode : setEditedItem}
                                    isEditMode={isPropSortCodeEditing}
                                    handleSaveChange={isPropSortCodeAdding ? handlePropSortCodeSave : saveSortCodeEdit}
                                    handleClose={() => {
                                        setIsPropSortCodeAdding(false);
                                        setIsPropSortCodeEditing(false);
                                    }}
                                    contentKey={"SortingCode"}
                                    formattedSortCodesGroupNamesOptions={formattedSortCodesGroupNamesOptions}
                                    formattedSortCodeVatType={formattedSortCodeVatType}
                                    filteredCode6111OptionsForSortCode={filteredCode6111OptionsForSortCode}
                                    direction={direction}
                                    t={t}
                                    handleEdit={handleEdit}
                                    handleDelete={handleDelete}
                                    existingSortCodes={sortCodesOptions}
                                />
                            )}
                        </div>

                        <div className={styles.propertyCard}>
                            <div className={styles.propertyHeader}>
                                <div className={styles.propertyTitleWrap}>
                                    <span className={styles.cardIcon}><DescriptionOutlinedIcon /></span>
                                    <h3 className={styles.propertyTitle}>{t('PalCode')}</h3>
                                </div>
                                <div className={styles.propertyActions}>
                                    <input type="file" ref={fileInputRef2} style={{ display: 'none' }} />
                                    <IconButton className={styles.propertyActionBtn} onClick={() => setIsPropPalCodeAdding(true)}>
                                        <img src={addIconImage} alt="Add" />
                                    </IconButton>
                                    <IconButton className={styles.propertyActionBtnAlt} onClick={() => handleFileButtonClick(fileInputRef2, uploadFileToPalCode)}>
                                        <img src={importIconImage} alt="Import" />
                                    </IconButton>
                                    <Tooltip title={t('DownloadTemplate')} arrow>
                                        <IconButton
                                            className={styles.propertyActionBtn}
                                            onClick={() => handleDownloadTemplate(palCodeFile, "Xtram_Palcode_Template.xlsx")}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                            <p className={styles.propertyHint}>{t('SelectBalanceClassification')}</p>
                            {!isPropPalCodeAdding && !isPropPalCodeEditing ? (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    endIcon={<TableViewIcon />}
                                    className={styles.viewAllBtn}
                                    onClick={() => handleViewTable(formattedPalCodeOptions, 'PalCode')}
                                >
                                    {t("ViewAllPalCode")}
                                </Button>
                            ) : (
                                <AccountantDetailsModals
                                    isPopupOpen={isPropPalCodeAdding || isPropPalCodeEditing}
                                    value={isPropPalCodeAdding ? palCode : editedItem}
                                    handleValueChange={isPropPalCodeAdding ? setPalCode : setEditedItem}
                                    isEditMode={isPropPalCodeEditing}
                                    handleSaveChange={isPropPalCodeAdding ? handlePropPalCodeSave : savePalCodeEdit}
                                    handleClose={() => {
                                        setIsPropPalCodeAdding(false);
                                        setIsPropPalCodeEditing(false);
                                    }}
                                    contentKey={"PalCode"}
                                    formattedPalCodeCategoryOptions={palCodeCategoryOptions}
                                    filteredCode6111Options={filteredCode6111Options}
                                    onPalCodeTypeChange={fetchCode6111ByType}
                                    Constants={Constants}
                                    customStyles={customStyles}
                                    t={t}
                                    direction={direction}
                                    existingPalCodes={palCodeOptions}
                                />
                            )}
                        </div>

                        <div className={styles.propertyCard}>
                            <div className={styles.propertyHeader}>
                                <div className={styles.propertyTitleWrap}>
                                    <span className={styles.cardIcon}><DescriptionOutlinedIcon /></span>
                                    <h3 className={styles.propertyTitle}>{t('OtherDocumentsType')}</h3>
                                </div>
                                <div className={styles.propertyActions}>
                                    <IconButton className={styles.propertyActionBtn} onClick={() => setIsPropOtherDocumentsTypeAdding(true)}>
                                        <img src={addIconImage} alt="Add" />
                                    </IconButton>
                                </div>
                            </div>
                            <p className={styles.propertyHint}>{t('Select OtherDocumentsType')}</p>
                            {!isPropOtherDocumentsTypeAdding && !isPropOtherDocumentsTypeEditing ? (
                                <AutocompleteCardSelect
                                    options={formattedOtherDocumentsTypeOptions}
                                    selectedValues={selectedOtherDocumentsType}
                                    onChange={(event, newValue) => setSelectedOtherDocumentsType(newValue)}
                                    label={t("OtherDocumentsType")}
                                    placeholder={t('Select OtherDocumentsType')}
                                    isMultiple={false}
                                />
                            ) : (
                                (isPropOtherDocumentsTypeAdding || isPropOtherDocumentsTypeEditing) ? (
                                    <AccountantDetailsModals
                                        isPopupOpen={isPropOtherDocumentsTypeAdding || isPropOtherDocumentsTypeEditing}
                                        value={isPropOtherDocumentsTypeAdding ? otherDocumentsType : editedItem.otherDocumentTypeName}
                                        handleValueChange={isPropOtherDocumentsTypeAdding
                                            ? setOtherDocumentsType
                                            : (newValue) => setEditedItem({ ...editedItem, otherDocumentTypeName: newValue })
                                        }
                                        isEditMode={isPropOtherDocumentsTypeEditing}
                                        handleSaveChange={isPropOtherDocumentsTypeAdding
                                            ? handlePropOtherDocumentsTypeSave
                                            : savePropOtherDocumentsTypeEdit
                                        }
                                        handleClose={() => {
                                            setIsPropOtherDocumentsTypeAdding(false);
                                            setIsPropOtherDocumentsTypeEditing(false);
                                        }}
                                        contentKey={"OtherDocumentsType"}
                                        existingOtherDocumentsType={otherDocumentsTypeOptions}
                                    />
                                ) : null
                            )}
                        </div>

                        <OtherDocumentsTitlesCard
                            t={t}
                            direction={direction}
                            styles={styles}
                            user={user}
                            token={token}
                            types={otherDocumentsTypeOptions}
                            onTypesChanged={() => fetchEnums(token, user)}
                        />
                    </div>

            </div>

            <CustomDeleteConfirmationModal
                isVisible={isModalVisible}
                onClose={() => { setIsModalVisible(false); setItemIdToDelete(null); }}
                onDelete={handleConfirm}
            />

            <ToastContainer />
            {loading && (
                <div className={styles.loader}>
                    <PuffLoader
                        size={100}
                        loading={loading}
                    />
                </div>
            )}

            <SortCodeUsageModal
                direction={direction}
                isOpen={sortCodeUsageModal}
                onClose={() => setSortCodeUsageModal(false)}
                sortCodeId={sortCodeUsageData?.sortCodeId}
                sortCodeName={sortCodeUsageData?.sortCodeName}
                usageDetails={sortCodeUsageData?.usageDetails}
                totalCount={sortCodeUsageData?.totalCount}
            />
        </>
    );
};

export default AccountantDetails;
